import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { AlertCircle, Loader2, Lock, Clock, XCircle, CheckCircle } from 'lucide-react';
import api from '../../services/api';
import { successAlert, errorAlert } from '../../utils/alerts';
import AuthLayout from './AuthLayout';

const ActivateAccount = () => {
  const { key } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [userInfo, setUserInfo] = useState(null);
  const [errorInfo, setErrorInfo] = useState({ code: '', detail: '' });

  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const validateKey = async () => {
      try {
        const response = await api.get(`/auth/activate/${key}/`);
        setUserInfo(response.data);
        setStatus('ready');
      } catch (err) {
        setErrorInfo({
          code: err.response?.data?.code || 'invalid_key',
          detail: err.response?.data?.detail || 'Link de ativação inválido.',
        });
        setStatus('error');
      }
    };
    validateKey();
  }, [key]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('A senha deve ter no mínimo 8 caracteres.');
      return;
    }
    if (password !== password2) {
      setError('As senhas não conferem.');
      return;
    }

    try {
      setSubmitting(true);
      await api.post('/auth/activate/', {
        activation_key: key,
        password,
        password2,
      });
      await successAlert('Conta ativada!', 'Você já pode fazer login.');
      navigate('/login');
    } catch (err) {
      const data = err.response?.data;
      const message =
        data?.detail ||
        (Array.isArray(data?.password) ? data.password[0] : null) ||
        'Não foi possível ativar a conta. Tente novamente.';
      errorAlert('Erro ao ativar conta', message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Tela de carregamento ──────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <AuthLayout title="Ativação de conta">
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <Loader2 size={32} className="animate-spin text-blue-600" />
          <p className="text-sm text-gray-500">Validando seu link de ativação...</p>
        </div>
      </AuthLayout>
    );
  }

  // ── Tela de erro (link expirado / já ativado / inválido) ─────────────────
  if (status === 'error') {
    const { code, detail } = errorInfo;
    const isExpired = code === 'expired';
    const isActivated = code === 'already_activated';
    const Icon = isExpired ? Clock : isActivated ? CheckCircle : XCircle;
    const iconColor = isActivated ? 'text-green-500' : isExpired ? 'text-amber-500' : 'text-red-500';

    return (
      <AuthLayout title="Ativação de conta">
        <div className="flex flex-col items-center text-center py-4 gap-4">
          <Icon size={48} className={iconColor} />
          <p className="text-sm text-gray-700">{detail}</p>
          {isExpired && (
            <p className="text-sm text-gray-500">
              Solicite um novo link de ativação ao seu gestor.
            </p>
          )}
          <Link
            to="/login"
            className="mt-2 w-full py-3 px-4 bg-[#1e3a5f] hover:bg-[#162d49] text-white font-semibold rounded-xl shadow-md shadow-blue-900/20 transition-all duration-200 text-center text-sm"
          >
            Ir para login
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // ── Formulário de definição de senha ─────────────────────────────────────
  return (
    <AuthLayout title="Ative sua conta">
      <div className="mb-6">
        <p className="text-gray-800 font-medium">Olá, {userInfo?.name || userInfo?.username}!</p>
        {userInfo?.email && (
          <p className="text-sm text-gray-500 mt-0.5">{userInfo.email}</p>
        )}
        <p className="text-sm text-gray-500 mt-2">Defina sua senha para concluir a ativação.</p>
      </div>

      {error && (
        <div className="mb-5 flex items-start gap-3 bg-red-50/80 border-l-4 border-red-500 p-4 rounded-r-lg text-sm text-red-700 shadow-sm">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
            Nova senha
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 8 caracteres"
              autoComplete="new-password"
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50/50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 focus:bg-white transition-all"
              disabled={submitting}
            />
          </div>
        </div>

        <div>
          <label htmlFor="password2" className="block text-sm font-medium text-gray-700 mb-1.5">
            Confirmar senha
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-gray-400" />
            </div>
            <input
              id="password2"
              type="password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              placeholder="Repita a senha"
              autoComplete="new-password"
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50/50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 focus:bg-white transition-all"
              disabled={submitting}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-3.5 px-4 bg-[#1e3a5f] hover:bg-[#162d49] active:bg-[#12253d] text-white font-semibold rounded-xl shadow-md shadow-blue-900/20 hover:shadow-lg hover:shadow-blue-900/30 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Ativando...
            </>
          ) : (
            'Ativar conta'
          )}
        </button>
      </form>
    </AuthLayout>
  );
};

export default ActivateAccount;
